import React from "react";
import {BlobProvider} from "@react-pdf/renderer";
import {faFileAlt} from "@fortawesome/fontawesome-free-solid";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Button, EmptyView, MenuQuinzaineBilling, MenuYearBilling, Paper, Spinner, Title} from "../../components";
import {ReactComponent as ImportedSVG} from "../../assets/pictures/invoice.svg";
import {EMPTY_INVOICE_TEXT, EMPTY_INVOICE_TITLE} from "../../constants/dishopInfo";
import InvoiceDocument from "../../components/invoice_document";
import {IconProp} from "@fortawesome/fontawesome-svg-core";
import {withStyles} from "@material-ui/core/styles";
import {styles} from "./Invoice.styles";
import {useInvoiceViewModel} from "./invoiceHooks";

type Props = {
    classes?: Record<string, string>;
};

const InvoiceView = (props: Props) => {
    const {classes: classesProp} = props;
    const classes = (classesProp ?? {}) as Record<string, string>;

    const vm = useInvoiceViewModel();

    if (vm.isLoading) {
        return (
            <div className="px-4 pb-4">
                <Title title="Factures" onClick={vm.handleHelp}/>
                <div style={{display: "flex", justifyContent: "center"}} className={classes.progress}>
                    <Spinner/>
                </div>
            </div>
        );
    }

    return (
        <div className="px-4 pb-4">
            <Title title="Factures" onClick={vm.handleHelp}/>
            <div className="py-3">
                <Paper className={classes.root}>
                    <div className="pb-3 d-flex flex-row">
                        <Button
                            variant="contained"
                            color="secondary"
                            outlined
                            className="bg-white"
                            onClick={vm.handleClick}
                            disabled={!vm.hasPeriods}
                        >
                            {vm.periodeLabel}
                        </Button>

                        <MenuQuinzaineBilling
                            optionFinance={vm.safeOptionFinance}
                            periode={vm.periodeLabel}
                            anchorEl={vm.anchorEl}
                            currentYear={vm.currentYear}
                            handleClose={vm.handleClose}
                            handleFetchData={vm.handleFetchData}
                        />

                        <Button
                            variant="contained"
                            color="secondary"
                            outlined
                            className="bg-white"
                            onClick={vm.handleClickYear}
                            disabled={!vm.hasPeriods}
                        >
                            {vm.currentYear}
                        </Button>

                        <MenuYearBilling
                            optionFinance={vm.safeOptionFinance}
                            anchorElYear={vm.anchorElYear}
                            currentYear={vm.currentYear}
                            handleClose={vm.handleClose}
                            handleUpdateYear={vm.handleUpdateYear}
                        />
                    </div>

                    {vm.loadingInvoice && (
                        <div style={{height: "1300px"}}>
                            <div className="mt-5" style={{display: "flex", justifyContent: "center"}}>
                                <Spinner/>
                            </div>
                        </div>
                    )}

                    {!vm.loadingInvoice &&
                        (vm.shouldRenderPdf
                            ? (
                                <BlobProvider document={<InvoiceDocument invoice={vm.invoice!} isNetwork={vm.isNetwork}/>}>
                                    {({url, loading}) => {
                                        const hasTouchscreen = "ontouchstart" in window;
                                        if (loading) {
                                            return "Loading...";
                                        }
                                        return hasTouchscreen
                                            ? (
                                                <Paper variant="outlined" className={classes.paper}>
                                                    <FontAwesomeIcon icon={faFileAlt as IconProp}
                                                                     className={classes.iconFile}/>
                                                    <Button outlined color="secondary"
                                                            onClick={() => window.open(url!)}>
                                                        Voir la facture
                                                    </Button>
                                                </Paper>
                                            )
                                            : (
                                                <embed width="100%" height="1300px" src={url!}/>
                                            );
                                    }}
                                </BlobProvider>
                            )
                            : (
                                <EmptyView className={classes.svg} src={ImportedSVG} title={EMPTY_INVOICE_TITLE}
                                           text={EMPTY_INVOICE_TEXT}/>
                            ))}
                </Paper>
            </div>
        </div>
    );
};

export default withStyles(styles)(InvoiceView);